/**
 * BCP-47 language tag handling.
 *
 * Availability is the backend's call (`localization-settings.available_languages`,
 * served by platform config v2), so tags are validated by shape and canonicalized
 * rather than matched against a closed set — a second FE allowlist could only
 * subtract from what the operator configured. The accepted shape deliberately
 * mirrors the admin-services S3-key guard (`_LOCALE_RE` in
 * `trivelta_helper/i18n/tcm_strings_manager.py`) so the two layers cannot
 * disagree about which tags are legal.
 *
 * Canonical casing matters: the tag doubles as the TCM V2 folder name in
 * `common_assets/strings/<lng>/<namespace>.json`.
 */
export const DEFAULT_LANGUAGE = "en";

/**
 * Locales with bundled i18next resources and curated display labels. This is the
 * selector fallback while platform config v2 is unavailable and the i18next
 * `supportedLngs` set — not a gate on what the backend may offer.
 */
export const FALLBACK_LANGUAGES = ["en", "pt-BR", "es", "de"] as const;

/**
 * `pt` means Brazilian Portuguese on this platform — a product decision, not a
 * canonicalization, so it stays explicit. `pt-br` and `pt_BR` need no entry:
 * canonicalization already yields `pt-BR`.
 */
const LANGUAGE_ALIASES: Record<string, string> = {
  pt: "pt-BR",
};

// BCP-47 well-formedness, held deliberately in step with `_LOCALE_RE` in
// admin-services (`trivelta_helper/i18n/tcm_strings_manager.py`): if this were
// stricter, the FE would drop tags the operator legitimately configured.
const TAG_RE = /^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/;

const SCRIPT_RE = /^[A-Za-z]{4}$/;
const REGION_RE = /^(?:[A-Za-z]{2}|\d{3})$/;

/** Per BCP-47: language lowercase, 4-alpha script Titlecase, region UPPERCASE. */
function canonicalizeSubtag(subtag: string, index: number): string {
  if (index === 0) return subtag.toLowerCase();
  if (SCRIPT_RE.test(subtag)) {
    return subtag[0].toUpperCase() + subtag.slice(1).toLowerCase();
  }
  if (REGION_RE.test(subtag)) return subtag.toUpperCase();
  return subtag.toLowerCase();
}

/**
 * Canonical form of a raw language value (API payload, localStorage, selector),
 * or `null` when it is missing or not tag-shaped — callers decide their own
 * fallback (precedence chains fall through; the platform-language path clamps
 * to `en`).
 */
export function canonicalizeLanguageTag(raw: unknown): string | null {
  if (raw == null || typeof raw !== "string") return null;

  const trimmed = raw.trim().replace(/_/g, "-");
  if (!TAG_RE.test(trimmed)) return null;

  const canonical = trimmed.split("-").map(canonicalizeSubtag).join("-");
  return LANGUAGE_ALIASES[canonical] ?? canonical;
}
